import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutGrid, Terminal, Activity, AlertTriangle, CalendarClock,
  Package, CalendarRange, Users, Cpu, BarChart3, UserCog, History, Settings as SettingsIcon,
  Globe, LogOut, ChevronLeft, ChevronRight, FileWarning, Trophy, Award, Gift,
  Bell, BookOpen,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useI18n, LANG_LABELS } from '../i18n/I18nContext';
import { LOGO } from '../mock/data';
import InstallButton from './InstallButton';
import { useWarehouse } from '../context/WarehouseContext';

const NAV = [
  { to: '/', tkey: 'nav.live', icon: LayoutGrid, end: true, roles: ['ADMIN', 'SUPERVISOR'] },
  { to: '/operator-terminal', tkey: 'nav.terminal', icon: Terminal, roles: ['ADMIN', 'SUPERVISOR', 'OPERATOR'] },
  { to: '/machine-logs', tkey: 'nav.logs', icon: Activity, roles: ['ADMIN', 'SUPERVISOR', 'OPERATOR'] },
  { to: '/problems', tkey: 'nav.problems', icon: AlertTriangle, roles: ['ADMIN', 'SUPERVISOR', 'OPERATOR'] },
  { to: '/scheduling', tkey: 'nav.scheduling', icon: CalendarClock, roles: ['ADMIN', 'SUPERVISOR', 'OPERATOR'] },
  { to: '/materials', tkey: 'nav.materials', icon: Package, roles: ['ADMIN', 'SUPERVISOR', 'OPERATOR'] },
  { to: '/material-schedule', tkey: 'nav.matschedule', icon: CalendarRange, roles: ['ADMIN', 'SUPERVISOR'] },
  { to: '/assignments', tkey: 'nav.assignments', icon: Users, roles: ['ADMIN', 'SUPERVISOR'] },
  { to: '/machines', tkey: 'nav.machines', icon: Cpu, roles: ['ADMIN', 'SUPERVISOR'] },
  { to: '/reports', tkey: 'nav.reports', icon: BarChart3, roles: ['ADMIN', 'SUPERVISOR'] },
  { to: '/problem-report', tkey: 'nav.problemReport', icon: FileWarning, roles: ['ADMIN', 'SUPERVISOR'] },
  { to: '/leaderboard', tkey: 'nav.leaderboard', icon: Trophy, roles: ['ADMIN', 'SUPERVISOR', 'OPERATOR'] },
  { to: '/my-awards', tkey: 'nav.myAwards', icon: Award, roles: ['ADMIN', 'SUPERVISOR', 'OPERATOR'] },
  { to: '/manage-awards', tkey: 'nav.manageAwards', icon: Gift, roles: ['ADMIN'] },
  { to: '/users', tkey: 'nav.users', icon: UserCog, roles: ['ADMIN'] },
  { to: '/audit', tkey: 'nav.audit', icon: History, roles: ['ADMIN', 'SUPERVISOR'] },
  { to: '/help', tkey: 'nav.help', icon: BookOpen, roles: ['ADMIN', 'SUPERVISOR', 'OPERATOR'] },
  { to: '/notifications', tkey: 'nav.notifications', icon: Bell, roles: ['OPERATOR'] },
  { to: '/settings', tkey: 'nav.settings', icon: SettingsIcon, roles: ['ADMIN', 'SUPERVISOR'] },
];

function WarehouseSwitch({ collapsed }) {
  const wh = useWarehouse();
  if (!wh?.active) return null;
  return (
    <div className="mb-3" data-testid="sidebar-warehouse">
      {!collapsed && <div className="kmt-label mb-1">Factory</div>}
      <div className="flex items-center gap-2 rounded-lg px-2.5 py-2" style={{ background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)' }}>
        <FileWarning size={15} style={{ color: 'var(--kmt-gold)' }} className="shrink-0" />
        {!collapsed && <>
          <span className="text-sm truncate flex-1" title={wh.active.name}>{wh.active.name}</span>
          {wh.canSwitch && <button onClick={() => wh.setActive('')} className="text-xs shrink-0" style={{ color: 'var(--kmt-gold)' }} data-testid="sidebar-switch-warehouse">Switch</button>}
        </>}
      </div>
    </div>
  );
}


export default function Sidebar({ mobileOpen = false, onClose = () => {} }) {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('kmt_sidebar_collapsed') === '1');
  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('kmt_sidebar_collapsed', next ? '1' : '0');
  };

  return (
    <aside className={`flex flex-col h-screen top-0 left-0 shrink-0 fixed md:sticky z-40 transition-transform duration-200 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
      style={{ width: mobileOpen ? 260 : (collapsed ? 72 : 240), background: 'var(--kmt-panel)', borderRight: '1px solid var(--kmt-border)' }}>
      {/* brand + collapse toggle */}
      <div className="flex items-center h-[68px] shrink-0 relative"
        style={{ borderBottom: '1px solid var(--kmt-border-soft)', paddingLeft: collapsed && !mobileOpen ? 0 : 20, paddingRight: collapsed && !mobileOpen ? 0 : 12, justifyContent: collapsed && !mobileOpen ? 'center' : 'space-between' }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center kmt-panel-2 shrink-0">
            <img src={LOGO} alt="KMT" className="w-6 h-6 object-contain" />
          </div>
          {(!collapsed || mobileOpen) && (
            <div>
              <div className="font-display font-bold text-lg leading-none" style={{ letterSpacing: '0.15em', color: 'var(--kmt-gold)' }}>KMT</div>
              <div className="kmt-label" style={{ fontSize: 9 }}>FASTENERS · FACTORY MNG</div>
            </div>
          )}
        </div>
        {(!collapsed || mobileOpen) && (
          <button onClick={mobileOpen ? onClose : toggle} title="Collapse menu" aria-label="Collapse menu"
            className="w-7 h-7 rounded-md flex items-center justify-center kmt-panel-2 hover:bg-[#2a2a30]" style={{ color: 'var(--kmt-muted)' }}>
            <ChevronLeft size={16} />
          </button>
        )}
      </div>

      {/* floating expand button when collapsed (desktop only) */}
      {collapsed && !mobileOpen && (
        <button onClick={toggle} title="Expand menu" aria-label="Expand menu"
          className="hidden md:flex absolute -right-3 top-[52px] w-6 h-6 rounded-full items-center justify-center z-20"
          style={{ background: 'var(--kmt-gold)', color: '#17140a', boxShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
          <ChevronRight size={15} />
        </button>
      )}

      {/* nav */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 space-y-1">
        {items.map((n) => {
          const Icon = n.icon;
          const iconOnly = collapsed && !mobileOpen;
          return (
            <NavLink key={n.to} to={n.to} end={n.end} title={iconOnly ? t(n.tkey) : undefined} onClick={onClose}
              className={({ isActive }) => `kmt-nav-item ${isActive ? 'active' : ''}`}
              style={{ justifyContent: iconOnly ? 'center' : 'flex-start', paddingLeft: iconOnly ? 0 : 14, paddingRight: iconOnly ? 0 : 14 }}>
              <Icon size={18} strokeWidth={1.9} className="shrink-0" />
              {!iconOnly && <span>{t(n.tkey)}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* lang + user */}
      <div className="px-3 pb-4 shrink-0">
        <WarehouseSwitch collapsed={collapsed && !mobileOpen} />
        <InstallButton collapsed={collapsed && !mobileOpen} />
        {(!collapsed || mobileOpen) && (
          <div className="flex items-center gap-1 kmt-panel-2 p-1 rounded-lg mb-3 w-fit">
            <Globe size={14} className="mx-1" style={{ color: 'var(--kmt-muted)' }} />
            {['EN', 'HI', 'PA'].map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`lang-btn-${l}`} className="px-2 py-0.5 rounded text-xs font-mono-k"
                style={{ background: lang === l ? 'var(--kmt-gold)' : 'transparent', color: lang === l ? '#17140a' : 'var(--kmt-muted)', fontWeight: 600 }}>
                {LANG_LABELS[l]}
              </button>
            ))}
          </div>
        )}
        {collapsed && !mobileOpen ? (
          <div className="flex flex-col items-center gap-3">
            <div className="w-9 h-9 rounded-md flex items-center justify-center font-display font-bold" title={`${user?.name} · ${user?.role}`}
              style={{ background: 'var(--kmt-gold)', color: '#17140a' }}>
              {user?.name?.[0] || 'U'}
            </div>
            <button onClick={logout} title="Logout" className="p-1.5 rounded-md hover:bg-[#2a2a30]" style={{ color: 'var(--kmt-muted)' }}>
              <LogOut size={16} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3 kmt-panel-2 p-2.5 rounded-lg">
            <div className="w-8 h-8 rounded-md flex items-center justify-center font-display font-bold shrink-0" style={{ background: 'var(--kmt-gold)', color: '#17140a' }}>
              {user?.name?.[0] || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="kmt-label" style={{ fontSize: 9, color: 'var(--kmt-gold-dim)' }}>{user?.role}</div>
            </div>
            <button onClick={logout} title="Logout" className="p-1.5 rounded-md hover:bg-[#2a2a30]" style={{ color: 'var(--kmt-muted)' }}>
              <LogOut size={16} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
